/**
 * @fileoverview Trigger summary row (MOR-736)
 *
 * Renders an automation's "When" trigger with the event phrase as plain text
 * and only value-like elements (statuses, send statuses, search types, task
 * titles) as badges — task statuses in their own assigned colour. Shared by
 * the automations manager (general table + workflow groups) and the workflow
 * editor's automations section.
 */

import { useMemo } from "react";
import type { AutomationTrigger } from "@cvx/automations/automationRules/schema";
import { Badge } from "@/ui/primitives";
import type { BadgeColor } from "@/ui/primitives/badge";
import { triggerTokens } from "./automation-config";

export interface TriggerStatus {
  key: string;
  name: string;
  color?: string;
}

export function TriggerBadges({
  trigger,
  statuses,
  showWhen = false,
  className,
}: {
  trigger: AutomationTrigger;
  /** Org task statuses, used to colour + name status filter badges. */
  statuses?: ReadonlyArray<TriggerStatus>;
  /** Prefix the row with a muted "When" label. */
  showWhen?: boolean;
  className?: string;
}) {
  const resolveStatus = useMemo(() => {
    const map = new Map(statuses?.map((s) => [s.key, s]) ?? []);
    return (key: string) => {
      const status = map.get(key);
      return status
        ? { name: status.name, color: status.color as BadgeColor | undefined }
        : undefined;
    };
  }, [statuses]);

  const tokens = triggerTokens(trigger, resolveStatus);

  return (
    <div
      className={
        className ??
        "flex flex-wrap items-center gap-1 text-sm text-muted-foreground"
      }
    >
      {showWhen && <span className="text-xs text-muted-foreground">When</span>}
      {tokens.map((token, i) =>
        token.kind === "text" ? (
          <span key={i} className="text-xs text-muted-foreground">
            {token.text}
          </span>
        ) : (
          <Badge
            key={i}
            variant="soft"
            color={token.color}
            className="font-normal"
          >
            {token.label}
          </Badge>
        ),
      )}
    </div>
  );
}
